import { useEffect, useState } from "react";
import { api, getAxiosErrorMessage } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LIBELLES = {
  fournisseur: { titre: "Créer un litige fournisseur", tiers: "Fournisseur" },
  // `titre` désigne la TYPOLOGIE du litige (« Transport »), `tiers` désigne
  // l'ENTREPRISE mise en cause (« Transporteur »). La distinction n'est pas
  // cosmétique : le champ demande de choisir une société, pas une catégorie.
  transporteur: { titre: "Créer un litige transport", tiers: "Transporteur" },
};

/**
 * Dérivation d'un litige client vers un fournisseur ou un transporteur.
 *
 * Le formulaire est prérempli à partir du litige d'origine, et entièrement
 * modifiable : rien n'est créé tant que l'utilisateur n'a pas confirmé. Le lien
 * avec le litige d'origine est posé par le serveur, pas par ce formulaire.
 */
export default function DeriveComplaintDialog({
  complaintId, type, open, onOpenChange, onCreated,
}) {
  const [form, setForm] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [origine, setOrigine] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const libelles = LIBELLES[type];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    let annule = false;
    (async () => {
      try {
        const kinds = type === "fournisseur"
          ? ["fournisseur", "les_deux"]
          : ["transporteur", "les_deux"];
        const [modele, ...listes] = await Promise.all([
          api.get(`/complaints/${complaintId}/derive-template`, { params: { type } }),
          ...kinds.map((k) => api.get("/suppliers", { params: { kind: k } })),
        ]);
        if (annule) return;
        setOrigine({
          number: modele.data.parent_number,
          client: modele.data.parent_client_name,
        });
        setForm({
          sub_type: modele.data.sub_type || "",
          description: modele.data.description || "",
          severity: modele.data.severity || "moyenne",
          product_ref: modele.data.product_ref || "",
          tiers_id: "",
          assigned_to: "",
        });
        setTiers(
          listes.flatMap((r) => r.data).sort((a, b) => a.name.localeCompare(b.name))
        );
        const { data } = await api.get("/users/assignable");
        if (!annule) setResponsables(data);
      } catch (e) {
        if (!annule) toast.error(getAxiosErrorMessage(e));
      }
    })();
    return () => { annule = true; };
  }, [open, complaintId, type]);

  // Responsable du tiers choisi : proposé, visible, modifiable. Jamais imposé.
  const responsableDuTiers = tiers.find((t) => t.id === form?.tiers_id)?.responsible_user_id;
  const responsableEffectif = form?.assigned_to || responsableDuTiers || "";

  const soumettre = async (e) => {
    e.preventDefault();
    if (!form.tiers_id) {
      toast.error(`Choisissez un ${type}`);
      return;
    }
    setEnvoi(true);
    try {
      await api.post(`/complaints/${complaintId}/derive`, {
        type,
        [type === "fournisseur" ? "supplier_id" : "carrier_id"]: form.tiers_id,
        sub_type: form.sub_type,
        description: form.description,
        severity: form.severity,
        product_ref: form.product_ref,
        assigned_to: responsableEffectif || null,
      });
      toast.success(`Litige ${type} créé`);
      onCreated?.();
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{libelles.titre}</DialogTitle>
        </DialogHeader>

        {!form ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <form onSubmit={soumettre} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 p-3 text-sm text-slate-700">
              Dérivée de <strong>{origine?.number}</strong>
              {origine?.client ? <> · client <strong>{origine.client}</strong></> : null}.
              Le lien sera conservé dans les deux sens. Les champs sont préremplis
              depuis le litige d'origine : modifiez-les librement avant de valider.
            </div>

            <div className="md:col-span-2">
              <Label>{libelles.tiers}</Label>
              <select
                value={form.tiers_id}
                onChange={(e) => set("tiers_id", e.target.value)}
                required
                className="w-full h-10 rounded-xl mt-1.5 border border-slate-200 bg-white px-3 text-sm"
                data-testid="derive-tiers-select"
              >
                <option value="">Choisir…</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {tiers.length === 0 ? (
                <p className="text-xs text-amber-700 mt-1.5">
                  Aucun {type} enregistré. Créez la fiche depuis Fournisseurs.
                </p>
              ) : null}
            </div>

            <div>
              <Label>Motif</Label>
              <Input
                value={form.sub_type}
                onChange={(e) => set("sub_type", e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid="derive-subtype-input"
              />
            </div>
            <div>
              <Label>Gravité</Label>
              <select
                value={form.severity}
                onChange={(e) => set("severity", e.target.value)}
                className="w-full h-10 rounded-xl mt-1.5 border border-slate-200 bg-white px-3 text-sm"
                data-testid="derive-severity-select"
              >
                <option value="faible">Faible</option>
                <option value="moyenne">Moyenne</option>
                <option value="critique">Critique</option>
              </select>
            </div>

            <div>
              <Label>Référence produit</Label>
              <Input
                value={form.product_ref}
                onChange={(e) => set("product_ref", e.target.value)}
                className="rounded-xl mt-1.5"
              />
            </div>
            <div>
              <Label>Responsable</Label>
              <select
                value={responsableEffectif}
                onChange={(e) => set("assigned_to", e.target.value)}
                className="w-full h-10 rounded-xl mt-1.5 border border-slate-200 bg-white px-3 text-sm"
                data-testid="derive-assigned-select"
              >
                <option value="">Aucun</option>
                {responsables.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              {responsableDuTiers && !form.assigned_to ? (
                <p className="text-xs text-blue-700 mt-1.5" data-testid="derive-responsible-hint">
                  Proposé automatiquement : responsable de ce {type}. Modifiable.
                </p>
              ) : null}
            </div>

            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={5}
                required
                className="rounded-xl mt-1.5"
                data-testid="derive-description-input"
              />
            </div>

            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={envoi}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                data-testid="derive-submit-btn"
              >
                {envoi ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Créer le litige
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
